/**
 * +ranger-specs: aura tallies (docs/design/classes/specs.md "Grovewarden"). An aura with `tally`
 * counts toward a stacking aura on the same unit from the same caster: while N tallying instances
 * live there, the tally aura holds N stacks (capped at its maxStacks); it goes with the last one.
 *
 * The Grovewarden's Thicket tallies its heals-over-time: every HoT kind on an ally is one stack of
 * +8% healing taken. Recounted (not incremented) on every new instance and every removal, so
 * refreshes, Burstbloom eating a HoT, dispels and deaths can never drift it. Deterministic and
 * event-clean: the tally aura emits its usual apply / stack / remove events.
 */
import type { AuraDef, AuraInstance, SimContext, Unit, UnitId } from '../contracts';
import { applyAuraDef, removeInstance, removeStacks } from './auras';

/** Tallying instances on `u` for `tallyId` from `casterId`. */
function countFor(ctx: SimContext, u: Unit, tallyId: string, casterId: UnitId): number {
  let n = 0;
  for (const inst of u.auras) if (inst.casterId === casterId && ctx.content.aura(inst.auraId)?.tally === tallyId) n++;
  return n;
}

/** Bring the tally aura `def.tally` on `u` (from `casterId`) in line with the live tallying instances. */
export function retally(ctx: SimContext, u: Unit, def: AuraDef, casterId: UnitId, depth = 0): void {
  const tallyId = def.tally;
  if (!tallyId) return;
  const tdef = ctx.content.aura(tallyId);
  if (!tdef || tdef.tally) return; // a tally never tallies itself
  const want = Math.min(tdef.maxStacks ?? 1, countFor(ctx, u, tallyId, casterId));
  const have: AuraInstance | undefined = u.auras.find((a) => a.auraId === tallyId && a.casterId === casterId);
  const cur = have?.stacks ?? 0;
  if (want === cur) return;
  if (want <= 0) {
    if (have) removeInstance(ctx, u, have, 'expired');
    return;
  }
  if (!u.alive) return;
  if (want > cur) applyAuraDef(ctx, tdef, u.id, casterId, tallyId, { stacks: want - cur, depth: depth + 1 });
  else if (have) removeStacks(ctx, u, have, cur - want, 'expired');
}
